import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../state/api';
import { Card, Banner } from '../components/ui';

/**
 * Opinionated strategy compiler UX — guided Plan Context + Plan Contract drafting.
 * Full multi-screen wizard can deepen; this ships the compile → audit → activate loop.
 */
export default function PlanConfigurator() {
  const [planTypes, setPlanTypes] = useState([]);
  const [contract, setContract] = useState(null);
  const [context, setContext] = useState(null);
  const [nexusObjects, setNexusObjects] = useState([]);
  const [compile, setCompile] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');

  const refresh = async () => {
    const [types, c, ctx, nexus] = await Promise.all([
      api.planOsPlanTypes(),
      api.planOsGetContract(),
      api.planOsGetContext(),
      api.planOsListNexusContext(),
    ]);
    setPlanTypes(types.items || []);
    setContract(c.contract);
    setContext(ctx.context);
    setNexusObjects(nexus.items || []);
  };

  useEffect(() => {
    refresh().catch((e) => setErr(e.message || String(e)));
  }, []);

  const draft = useMemo(() => {
    return (
      contract || {
        planType: 'custom_outcome',
        strategicIntent: {
          triggeringOpportunityOrProblem: '',
          strategicContext: '',
          planningHorizon: '',
          scope: '',
          outOfScope: '',
          assumptions: [],
          strategicPrinciples: [],
        },
        strategicBet: {
          statement: '',
          forWhom: '',
          becauseOfContext: '',
          duringPeriod: '',
          provenance: 'PLAN_HYPOTHESIS',
        },
        primaryDesiredOutcome: {
          name: '',
          notTrueToday: '',
          whoOrWhatChanges: '',
          observableChange: '',
          targetDate: '',
          independentlyReviewable: true,
          owner: '',
          successDefinition: '',
          failureDefinition: '',
          priority: 'PRIMARY',
          status: 'NOT_STARTED',
        },
        outcomeConditions: [],
        milestones: [],
        metrics: [],
        assumptions: [],
        knownConstraints: [],
        resourceRules: [],
        governance: {},
        failurePolicy: {
          trigger: '',
          requiredAssessment: '',
          allowedDecisions: ['STOP', 'PIVOT', 'EXTEND', 'RESCOPE'],
        },
        workHypotheses: [],
      }
    );
  }, [contract]);

  const save = async () => {
    setBusy('save');
    setErr('');
    try {
      const res = await api.planOsPutContract(draft);
      setContract(res.contract);
      setMsg('Draft saved.');
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy('');
    }
  };

  const runCompile = async () => {
    setBusy('compile');
    setErr('');
    try {
      await api.planOsPutContract(draft);
      const res = await api.planOsCompile([]);
      setCompile(res);
      setMsg(res.canActivate ? 'Compile passed — ready to activate.' : 'Compile found blocking issues.');
      await refresh();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy('');
    }
  };

  const activate = async () => {
    setBusy('activate');
    setErr('');
    try {
      const res = await api.planOsActivate([]);
      setContract(res.contract);
      setCompile(res.compile);
      setMsg('Plan Contract activated. Reload the app to refresh the strategy lock chip.');
      await refresh();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy('');
    }
  };

  const migrate = async () => {
    setBusy('migrate');
    setErr('');
    try {
      const res = await api.planOsMigrationPropose();
      setMsg(`Legacy proposal drafted (${res.analysis?.length || 0} notes). Review before activate.`);
      await refresh();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy('');
    }
  };

  const syncNexusContext = async () => {
    setBusy('sync-context');
    setErr('');
    try {
      const result = await api.planOsSyncNexusContextCenter({
        applyToPlanContext: true,
        planType: draft.planType || 'custom_outcome',
      });
      if (result.context) setContext(result.context);
      setMsg(
        `Nexus Context Center synced: ${result.created} added, ${result.updated} updated, ${result.unchanged} unchanged. Plan Context filled from products / packaging / ICPs / personas / intent topics.`,
      );
      await refresh();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy('');
    }
  };

  const setPath = (path, value) => {
    setContract((prev) => {
      const base = { ...(prev || draft) };
      const parts = path.split('.');
      let cur = base;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return { ...base };
    });
  };

  return (
    <div className="page stack gap-lg" data-testid="plan-configurator">
      <header>
        <h1>Plan Configurator</h1>
        <p className="muted">
          Opinionated strategy compiler: Nexus Context → Strategic Bet → Desired Outcome → Conditions →
          Evidence → Gates → Assumptions → Constraints → Metrics → Work → Strategy Compile → Activate.
        </p>
      </header>

      {msg ? <Banner tone="ok">{msg}</Banner> : null}
      {err ? <Banner tone="warn">{err}</Banner> : null}

      <Card title="Plan Type">
        <select
          value={draft.planType || 'custom_outcome'}
          onChange={(e) => setPath('planType', e.target.value)}
        >
          {planTypes.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <p className="muted small">
          Context status: {context?.planId ? `captured ${context.capturedAt}` : 'not set'}
        </p>
        <button type="button" className="btn sm" disabled={!!busy} onClick={syncNexusContext}>
          {busy === 'sync-context' ? 'Refreshing Nexus…' : 'Refresh from Nexus Context Center'}
        </button>
      </Card>

      <Card title="Nexus Context">
        <p className="muted small">
          Read-only mirrors of products, packaging (offerings), ICPs, personas, and intent topics.
          Configure them in Nexus → AI Context Center — not here.
        </p>
        {nexusObjects.length === 0 ? (
          <p className="muted">
            No synced Nexus context yet — optional. You can still author Intent / Bet / Outcome / Conditions here and
            Activate without Nexus.
          </p>
        ) : (
          <ul className="stack gap-xs">
            {nexusObjects
              .filter((o) => o.sync?.syncStatus === 'active')
              .map((o) => (
                <li key={o.id}>
                  <strong>{o.nexusType}</strong>: {o.name}
                  {o.summary ? <span className="muted"> — {o.summary}</span> : null}
                </li>
              ))}
          </ul>
        )}
        {context ? (
          <p className="muted small" style={{ marginTop: 8 }}>
            Plan Context selection — products: {context.productRefs?.length || 0}, offerings:{' '}
            {context.offeringRefs?.length || 0}, segments: {context.customerSegmentRefs?.length || 0},
            stakeholders: {context.stakeholderRefs?.length || 0}, use cases:{' '}
            {context.useCaseRefs?.length || 0}
          </p>
        ) : null}
      </Card>

      <Card title="Strategic Intent">
        <label>
          Why now?
          <textarea
            rows={2}
            value={draft.strategicIntent?.triggeringOpportunityOrProblem || ''}
            onChange={(e) => setPath('strategicIntent.triggeringOpportunityOrProblem', e.target.value)}
          />
        </label>
        <label>
          Horizon
          <input
            value={draft.strategicIntent?.planningHorizon || ''}
            onChange={(e) => setPath('strategicIntent.planningHorizon', e.target.value)}
          />
        </label>
      </Card>

      <Card title="Strategic Bet">
        <p className="muted">Plan hypothesis — not Nexus canonical truth.</p>
        <textarea
          rows={3}
          placeholder="What do we believe will happen, for whom, because of the selected Nexus context, during this period?"
          value={draft.strategicBet?.statement || ''}
          onChange={(e) => setPath('strategicBet.statement', e.target.value)}
        />
        <input
          placeholder="For whom"
          value={draft.strategicBet?.forWhom || ''}
          onChange={(e) => setPath('strategicBet.forWhom', e.target.value)}
        />
      </Card>

      <Card title="Primary Desired Outcome (guided)">
        <label>
          What is not true today that must become true?
          <textarea
            rows={2}
            value={draft.primaryDesiredOutcome?.notTrueToday || ''}
            onChange={(e) => setPath('primaryDesiredOutcome.notTrueToday', e.target.value)}
          />
        </label>
        <label>
          Who/what must change state?
          <input
            value={draft.primaryDesiredOutcome?.whoOrWhatChanges || ''}
            onChange={(e) => setPath('primaryDesiredOutcome.whoOrWhatChanges', e.target.value)}
          />
        </label>
        <label>
          Observable change that demonstrates success
          <textarea
            rows={2}
            value={draft.primaryDesiredOutcome?.observableChange || ''}
            onChange={(e) => setPath('primaryDesiredOutcome.observableChange', e.target.value)}
          />
        </label>
        <label>
          By when?
          <input
            type="date"
            value={(draft.primaryDesiredOutcome?.targetDate || '').slice(0, 10)}
            onChange={(e) => setPath('primaryDesiredOutcome.targetDate', e.target.value)}
          />
        </label>
        <label className="row gap-sm">
          <input
            type="checkbox"
            checked={draft.primaryDesiredOutcome?.independentlyReviewable !== false}
            onChange={(e) => setPath('primaryDesiredOutcome.independentlyReviewable', e.target.checked)}
          />
          An independent reviewer could determine whether it happened
        </label>
        {(compile?.aiFindings || []).some((f) => /ACTIVITY/i.test(f.problem || f.rationale || '')) ? (
          <Banner tone="amber">
            Primary Desired Outcome was challenged as ACTIVITY. Prefer state language: what is not true today,
            who/what changes state, and an observable end-state — not a list of tasks.
          </Banner>
        ) : null}
      </Card>

      <Card title="Outcome Conditions (required for Activate)">
        <p className="muted">
          Success must decompose into explicit conditions. At least one condition must be marked required.
        </p>
        {(draft.outcomeConditions || []).map((c, idx) => (
          <div key={c.id || idx} className="card-inset" style={{ marginBottom: 10, padding: 10, border: '1px solid var(--border, #ddd)' }}>
            <label>
              Name
              <input
                value={c.name || ''}
                onChange={(e) => {
                  const next = [...(draft.outcomeConditions || [])];
                  next[idx] = { ...next[idx], name: e.target.value };
                  setContract({ ...draft, outcomeConditions: next });
                }}
              />
            </label>
            <label>
              Description
              <textarea
                rows={2}
                value={c.description || ''}
                onChange={(e) => {
                  const next = [...(draft.outcomeConditions || [])];
                  next[idx] = { ...next[idx], description: e.target.value };
                  setContract({ ...draft, outcomeConditions: next });
                }}
              />
            </label>
            <label className="row gap-sm">
              <input
                type="checkbox"
                checked={!!c.required}
                onChange={(e) => {
                  const next = [...(draft.outcomeConditions || [])];
                  next[idx] = { ...next[idx], required: e.target.checked };
                  setContract({ ...draft, outcomeConditions: next });
                }}
              />
              Required
            </label>
            <button
              type="button"
              className="btn sm ghost"
              onClick={() => {
                const next = (draft.outcomeConditions || []).filter((_, i) => i !== idx);
                setContract({ ...draft, outcomeConditions: next });
              }}
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn sm"
          onClick={() => {
            const next = [
              ...(draft.outcomeConditions || []),
              {
                id: `oc-${Date.now()}`,
                desiredOutcomeId: draft.primaryDesiredOutcome?.id || '',
                name: '',
                description: '',
                required: true,
                status: 'NOT_STARTED',
                evidenceRequirement: {
                  evidenceType: '',
                  sourceOfTruth: '',
                  minimumRequirement: '',
                },
              },
            ];
            setContract({ ...draft, outcomeConditions: next });
          }}
        >
          Add Outcome Condition
        </button>
      </Card>

      <Card title="Failure policy (required for Activate)">
        <p className="muted">Failure must not be redefinable as success.</p>
        <label>
          Trigger — when do we declare failure?
          <textarea
            rows={2}
            value={draft.failurePolicy?.trigger || ''}
            onChange={(e) => setPath('failurePolicy.trigger', e.target.value)}
            placeholder="e.g. Day 30 with zero discoveries and no proposal path on Week-1 accounts"
          />
        </label>
        <label>
          Required assessment
          <textarea
            rows={2}
            value={draft.failurePolicy?.requiredAssessment || ''}
            onChange={(e) => setPath('failurePolicy.requiredAssessment', e.target.value)}
            placeholder="What written assessment is required before STOP / PIVOT / EXTEND / RESCOPE?"
          />
        </label>
        <label>
          Review date
          <input
            type="date"
            value={(draft.failurePolicy?.reviewDate || '').slice(0, 10)}
            onChange={(e) => setPath('failurePolicy.reviewDate', e.target.value)}
          />
        </label>
      </Card>

      <Card title="Actions">
        <div className="row gap-sm wrap">
          <button type="button" className="btn" disabled={!!busy} onClick={save}>
            {busy === 'save' ? 'Saving…' : 'Save draft'}
          </button>
          <button type="button" className="btn" disabled={!!busy} onClick={runCompile}>
            {busy === 'compile' ? 'Compiling…' : 'Strategy Compile'}
          </button>
          <button type="button" className="btn primary" disabled={!!busy} onClick={activate}>
            {busy === 'activate' ? 'Activating…' : 'Activate'}
          </button>
          <button type="button" className="btn" disabled={!!busy} onClick={migrate}>
            {busy === 'migrate' ? 'Migrating…' : 'Propose from legacy plan'}
          </button>
        </div>
      </Card>

      {compile ? (
        <Card title="Strategy Compile">
          <ol>
            {(compile.chain || []).map((s) => (
              <li key={s.step}>
                <strong>{s.step}</strong>: {s.summary}
              </li>
            ))}
          </ol>
          <h3>Blocking</h3>
          <ul>
            {(compile.deterministicFindings || [])
              .filter((f) => f.level === 'blocking')
              .map((f) => (
                <li key={f.id}>
                  {f.problem} — {f.rationale}
                </li>
              ))}
          </ul>
          <h3>AI / strategic challenges</h3>
          <ul>
            {(compile.aiFindings || []).map((f) => (
              <li key={f.id}>
                [{f.severity}] {f.problem}
              </li>
            ))}
          </ul>
          <p>
            Can activate: <strong>{String(compile.canActivate)}</strong>
          </p>
        </Card>
      ) : null}
    </div>
  );
}
